import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaFilter, FaRupeeSign } from "react-icons/fa";
import {
  FiEye,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiLoader,
  FiDownload,
} from "react-icons/fi";
import Select from "react-select";

import {
  useGetGstReportQuery,
  useDownloadGstReportMutation,
} from "../toolkitServices/invoiceApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";

const fmt2 = (n) => Number(n || 0).toFixed(2);
const money = (n) => `₹ ${fmt2(n)}`;
const dIN = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-IN");
};

const THEME = {
  primary: "#6b59c6",
  ring: "#7c3aed",
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 12,
    borderColor: state.isFocused ? THEME.ring : "#e5e7eb",
    boxShadow: state.isFocused ? `${THEME.ring}22 0 0 0 3px` : "none",
    "&:hover": { borderColor: THEME.ring },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 70,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 8,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function TableSkeletonRows() {
  return Array.from({ length: 8 }).map((_, i) => (
    <tr key={`sk-${i}`} className="border-t">
      <td className="px-3 py-3">
        <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="px-3 py-3">
        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="hidden sm:table-cell px-3 py-3">
        <div className="h-4 w-24 bg-slate-200 rounded animate-pulse" />
      </td>
      <td className="px-3 py-3 text-right">
        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse ml-auto" />
      </td>
      <td className="hidden md:table-cell px-3 py-3 text-right">
        <div className="h-4 w-16 bg-slate-200 rounded animate-pulse ml-auto" />
      </td>
      <td className="hidden md:table-cell px-3 py-3 text-right">
        <div className="h-4 w-16 bg-slate-200 rounded animate-pulse ml-auto" />
      </td>
      <td className="hidden md:table-cell px-3 py-3 text-right">
        <div className="h-4 w-16 bg-slate-200 rounded animate-pulse ml-auto" />
      </td>
      <td className="hidden lg:table-cell px-3 py-3 text-right">
        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse ml-auto" />
      </td>
      <td className="px-3 py-3 text-right">
        <div className="h-4 w-24 bg-slate-200 rounded animate-pulse ml-auto" />
      </td>
      <td className="px-3 py-3 text-center">
        <div className="h-8 w-16 bg-slate-200 rounded-full animate-pulse mx-auto" />
      </td>
    </tr>
  ));
}

function CompactSummaryBar({ summary }) {
  const items = [
    { label: "Invoices", value: summary?.invoiceCount || 0 },
    { label: "Taxable", value: money(summary?.taxableTotal || 0) },
    { label: "CGST", value: money(summary?.cgstTotal || 0) },
    { label: "SGST", value: money(summary?.sgstTotal || 0) },
    { label: "IGST", value: money(summary?.igstTotal || 0) },
    { label: "Total", value: money(summary?.grandTotal || 0), highlight: true },
  ];

  return (
    <div className="shrink-0 border-b bg-gradient-to-r from-slate-50 via-violet-50/60 to-slate-50 px-2.5 py-2 sm:px-4 sm:py-2.5">
      <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((item) => (
          <div
            key={item.label}
            className={`min-w-[112px] shrink-0 rounded-xl border px-3 py-2 shadow-sm ${
              item.highlight
                ? "border-violet-500 bg-gradient-to-r from-[#6b59c6] to-[#806dcc] text-white"
                : "border-violet-100 bg-white text-[#4b3d91]"
            }`}
          >
            <div
              className={`text-[9px] font-semibold uppercase tracking-wide ${
                item.highlight ? "text-white/80" : "text-slate-500"
              }`}
            >
              {item.label}
            </div>
            <div className="mt-0.5 truncate text-[11px] font-bold tabular-nums sm:text-sm">
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GstDetailsModal({ open, onClose, invoice, itemDetails = [] }) {
  const invId = invoice?._id;

  const invItems = useMemo(
    () => (itemDetails || []).filter((x) => String(x?._id) === String(invId)),
    [itemDetails, invId]
  );

  const taxable = invoice?.taxable || 0;
  const cgst = invoice?.cgst || 0;
  const sgst = invoice?.sgst || 0;
  const igst = invoice?.igst || 0;
  const totalGst = cgst + sgst + igst;
  const total = invoice?.total || 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/45" />

          <motion.div
            className="relative w-full max-w-[760px] bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden"
            initial={{ y: 16, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 16, scale: 0.98, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="px-4 sm:px-5 py-3 text-white"
              style={{
                background:
                  "linear-gradient(90deg, rgba(148,129,206,1) 0%, rgba(107,89,198,1) 100%)",
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm sm:text-base font-extrabold truncate">
                      {invoice?.invCode || "-"}
                    </div>

                    <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-white/15">
                      {dIN(invoice?.invDate)}
                    </span>

                    {invoice?.branchName ? (
                      <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-white/15">
                        {invoice.branchName}
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-1 text-[11px] text-white/85">
                    Taxable <b>{money(taxable)}</b> • GST <b>{money(totalGst)}</b> •{" "}
                    Total <b>{money(total)}</b>
                  </div>
                </div>

                <button
                  onClick={onClose}
                  className="shrink-0 p-2 rounded-full bg-white/15 hover:bg-white/25 transition"
                  aria-label="Close"
                  title="Close"
                >
                  <FiX />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-xl border bg-[#f7f4ff] border-[#e6defd] px-3 py-2">
                  <div className="text-[11px] text-slate-500">CGST</div>
                  <div className="text-sm font-extrabold text-[#4b3d91]">
                    {money(cgst)}
                  </div>
                </div>
                <div className="rounded-xl border bg-[#f7f4ff] border-[#e6defd] px-3 py-2">
                  <div className="text-[11px] text-slate-500">SGST</div>
                  <div className="text-sm font-extrabold text-[#4b3d91]">
                    {money(sgst)}
                  </div>
                </div>
                <div className="rounded-xl border bg-[#f7f4ff] border-[#e6defd] px-3 py-2">
                  <div className="text-[11px] text-slate-500">IGST</div>
                  <div className="text-sm font-extrabold text-[#4b3d91]">
                    {money(igst)}
                  </div>
                </div>
                <div className="rounded-xl border bg-white border-slate-200 px-3 py-2">
                  <div className="text-[11px] text-slate-500">Total GST</div>
                  <div className="text-sm font-extrabold text-slate-900">
                    {money(totalGst)}
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border overflow-hidden">
                <div className="px-4 py-2.5 flex items-center justify-between bg-[#efeafd]">
                  <div className="text-sm font-extrabold text-[#4b3d91]">
                    Item Details
                  </div>
                  <span className="text-[11px] font-semibold text-[#4b3d91] bg-white/70 px-2 py-0.5 rounded-full">
                    {invItems.length} item(s)
                  </span>
                </div>

                <div className="max-h-[42vh] sm:max-h-[46vh] overflow-y-auto">
                  {invItems.length === 0 ? (
                    <div className="p-5 text-sm text-slate-500 text-center">
                      No itemDetails found for this invoice
                    </div>
                  ) : (
                    <div className="divide-y">
                      {invItems.map((it, idx) => {
                        const itTaxable = it?.taxable || 0;
                        const itCgst = it?.cgst || 0;
                        const itSgst = it?.sgst || 0;
                        const itIgst = it?.igst || 0;
                        const itGst = itCgst + itSgst + itIgst;

                        return (
                          <div
                            key={`${it?.itemName || "item"}_${idx}`}
                            className="p-3"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="font-extrabold text-slate-900 truncate">
                                  {it?.itemName || "-"}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">
                                  HSN: {it?.hsn || "-"}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">
                                  Taxable {money(itTaxable)}
                                </div>
                              </div>

                              <div className="text-right text-[11px] text-slate-600">
                                <div className="font-bold text-slate-900">
                                  GST {money(itGst)}
                                </div>
                                <div>CGST {money(itCgst)}</div>
                                <div>SGST {money(itSgst)}</div>
                                <div>IGST {money(itIgst)}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="px-4 py-3 bg-white border-t">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="rounded-xl border border-slate-200 px-3 py-2">
                      <div className="text-[11px] text-slate-500">Taxable</div>
                      <div className="text-sm font-extrabold">{money(taxable)}</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 px-3 py-2">
                      <div className="text-[11px] text-slate-500">Total GST</div>
                      <div className="text-sm font-extrabold">{money(totalGst)}</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 px-3 py-2">
                      <div className="text-[11px] text-slate-500">Grand Total</div>
                      <div className="text-sm font-extrabold">{money(total)}</div>
                    </div>

                    <button
                      onClick={onClose}
                      className="rounded-xl text-white font-extrabold text-sm px-3 py-2 hover:opacity-95 transition"
                      style={{ backgroundColor: THEME.primary }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-3 text-center text-[11px] text-slate-400 sm:hidden">
                Tap outside to close
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function GstReport() {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [filters, setFilters] = useState({
    branchId: "",
    fromDate: "",
    toDate: "",
  });

  const [draftFilters, setDraftFilters] = useState({
    branchId: "",
    fromDate: "",
    toDate: "",
  });

  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceLimit, setInvoiceLimit] = useState(10);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [downloadGstReport, { isLoading: isDownloading }] =
    useDownloadGstReportMutation();

  const queryParams = useMemo(
    () => ({
      ...filters,
      invoicePage,
      invoiceLimit,
      itemPage: 1,
      itemLimit: 5000,
    }),
    [filters, invoicePage, invoiceLimit]
  );

  const { data, isFetching, isLoading, isError, error } =
    useGetGstReportQuery(queryParams, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
    refetchOnReconnect: false,
    });

  const { data: branches, isFetching: branchLoading } = useGetAllBranchesQuery();

  const branchOptions = useMemo(
    () =>
      branches?.data?.map((b) => ({
        label: b.branchName,
        value: b._id,
      })) || [],
    [branches]
  );

  const summary = data?.summary || {};
  const invoiceSummary = useMemo(() => data?.invoiceSummary || [], [data]);
  const itemDetails = data?.itemDetails || [];
  const invoicePagination = data?.invoiceSummaryPagination || {};

  const currentPage = Number(invoicePagination?.page || invoicePage || 1);
  const currentLimit = Number(invoicePagination?.limit || invoiceLimit || 10);
  const totalRecords = Number(invoicePagination?.totalRecords || 0);
  const totalPages = Number(invoicePagination?.totalPages || 1);
  const hasNextPage = Boolean(invoicePagination?.hasNextPage);
  const hasPrevPage = Boolean(invoicePagination?.hasPrevPage);

  const branchMap = useMemo(() => {
    const m = {};
    (branches?.data || []).forEach((b) => {
      m[String(b._id)] = b.branchName;
    });
    return m;
  }, [branches]);

  const rows = useMemo(() => {
    return (invoiceSummary || []).map((inv) => ({
      ...inv,
      taxable: inv?.taxable ?? inv?.taxableTotal ?? 0,
      cgst: inv?.cgst ?? inv?.cgstTotal ?? 0,
      sgst: inv?.sgst ?? inv?.sgstTotal ?? 0,
      igst: inv?.igst ?? inv?.igstTotal ?? 0,
      gstTotal:
        (inv?.cgst ?? inv?.cgstTotal ?? 0) +
        (inv?.sgst ?? inv?.sgstTotal ?? 0) +
        (inv?.igst ?? inv?.igstTotal ?? 0),
      branchName: branchMap[String(inv?.branchId)] || "-",
    }));
  }, [invoiceSummary, branchMap]);

  const hasInvalidDateRange = !!(
    draftFilters.fromDate &&
    draftFilters.toDate &&
    draftFilters.fromDate > draftFilters.toDate
  );

  const showingFrom = totalRecords === 0 ? 0 : (currentPage - 1) * currentLimit + 1;
  const showingTo = Math.min(currentPage * currentLimit, totalRecords);

  const openFilterModal = () => {
    setDraftFilters(filters);
    setIsFilterOpen(true);
  };

  const clearAppliedFilters = () => {
    setFilters({ branchId: "", fromDate: "", toDate: "" });
    setInvoicePage(1);
  };

  const openDetails = (inv) => {
    setSelectedInvoice(inv);
    setDetailOpen(true);
  };

  const handleDownload = async () => {
    try {
      const blob = await downloadGstReport({
        branchId: filters.branchId,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gst-report-${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("GST report download failed:", err);
    }
  };

  return (
    <div className="flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-gradient-to-r from-[#6b59c6] via-[#806dcc] to-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <FaRupeeSign className="text-sm" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold tracking-tight sm:text-lg">
              GST Report
            </h2>
            <p className="truncate text-[9px] text-violet-100 sm:text-[11px]">
              Invoice-wise GST summary
            </p>
          </div>
          {isFetching && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <FiLoader className="animate-spin" /> refreshing…
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-medium ring-1 ring-white/10 sm:inline-flex">
            {totalRecords} invoices
          </span>
          <button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
            title="Download Excel"
            className="inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-xl bg-white text-emerald-700 shadow-sm transition hover:bg-emerald-50 disabled:opacity-60 sm:w-auto sm:rounded-full sm:px-3"
            aria-label="Download Excel"
          >
            {isDownloading ? (
              <FiLoader className="animate-spin" />
            ) : (
              <FiDownload />
            )}
            <span className="hidden text-[11px] font-semibold sm:inline">
              {isDownloading ? "Downloading…" : "Download"}
            </span>
          </button>

          <button
            type="button"
            onClick={openFilterModal}
            title="Filters"
            className="inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 sm:w-auto sm:rounded-full sm:px-3"
            aria-label="Open filters"
          >
            <FaFilter className="text-xs" />
            <span className="hidden text-[11px] font-semibold sm:inline">
              Filters
            </span>
          </button>
        </div>
      </div>

      {/* Active filters */}
      {(filters.branchId || filters.fromDate || filters.toDate) && (
        <div className="flex shrink-0 items-center gap-2 border-b bg-slate-50 px-3 py-1.5 sm:px-4">
          <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {filters.branchId && (
              <FilterChip
                label={`Branch: ${branchMap[filters.branchId] || "Selected"}`}
              />
            )}
            {filters.fromDate && (
              <FilterChip label={`From: ${dIN(filters.fromDate)}`} />
            )}
            {filters.toDate && (
              <FilterChip label={`To: ${dIN(filters.toDate)}`} />
            )}
          </div>
          <button
            type="button"
            onClick={clearAppliedFilters}
            className="shrink-0 text-[10px] font-semibold text-[#6b59c6] hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* SUMMARY */}
      {!isLoading && !isFetching && rows.length > 0 && (
        <div className="shrink-0">
          <CompactSummaryBar summary={summary} />
        </div>
      )}

      {/* Invoices */}
      <div className="min-h-0 flex-1 overflow-auto bg-slate-50/70 p-2.5 sm:p-4">
        <div className="h-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[680px] text-xs sm:min-w-full sm:text-sm">
            <thead className="sticky top-0 z-10 bg-[#efeafd] text-[10px] uppercase tracking-wide text-[#4b3d91] sm:text-[11px]">
              <tr>
                <th className="px-3 py-2.5 text-left">Invoice</th>
                <th className="px-3 py-2.5 text-left">Date</th>
                <th className="hidden px-3 py-2.5 text-left sm:table-cell">
                  Branch
                </th>
                <th className="px-3 py-2.5 text-right">Taxable</th>
                <th className="hidden border-l-2 border-l-violet-200 bg-violet-50/60 px-3 py-2.5 text-right md:table-cell">
                  CGST
                </th>
                <th className="hidden border-l border-l-violet-100 bg-violet-50/40 px-3 py-2.5 text-right md:table-cell">
                  SGST
                </th>
                <th className="hidden border-l border-l-violet-100 bg-violet-50/40 px-3 py-2.5 text-right md:table-cell">
                  IGST
                </th>
                <th className="hidden border-l border-l-violet-100 bg-violet-50/40 px-3 py-2.5 text-right lg:table-cell">
                  Total GST
                </th>
                <th className="border-l-2 border-l-violet-300 bg-violet-100/70 px-3 py-2.5 text-right">
                  Grand Total
                </th>
                <th className="px-3 py-2.5 text-center">View</th>
              </tr>
            </thead>

            <tbody className="bg-white text-slate-700">
              {(isLoading || isFetching) && <TableSkeletonRows />}

              {!isLoading && !isFetching && isError && (
                <tr>
                  <td colSpan={10} className="py-10 text-center text-rose-600">
                    {error?.data?.message || "Unable to load GST report"}
                  </td>
                </tr>
              )}

              {!isLoading && !isFetching && !isError && rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-10 text-center text-slate-500">
                    No records found
                  </td>
                </tr>
              )}

              {!isLoading &&
                !isFetching &&
                !isError &&
                rows.map((inv) => (
                  <tr
                    key={inv._id}
                    className="border-t border-slate-100 transition hover:bg-violet-50/40"
                  >
                    <td className="px-3 py-3 font-medium">
                      <div className="font-semibold text-slate-900 truncate max-w-[110px] sm:max-w-none">
                        {inv.invCode}
                      </div>
                      <div className="sm:hidden text-[10px] text-slate-500 mt-0.5">
                        {inv.branchName}
                      </div>
                    </td>

                    <td className="px-3 py-3 whitespace-nowrap">{dIN(inv.invDate)}</td>

                    <td className="hidden sm:table-cell px-3 py-3">{inv.branchName}</td>

                    <td className="px-3 py-3 text-right font-semibold whitespace-nowrap">
                      {money(inv.taxable)}
                    </td>

                    <td className="hidden whitespace-nowrap border-l-2 border-l-violet-100 bg-violet-50/20 px-3 py-3 text-right md:table-cell">
                      {money(inv.cgst)}
                    </td>

                    <td className="hidden whitespace-nowrap border-l border-l-violet-100 bg-violet-50/20 px-3 py-3 text-right md:table-cell">
                      {money(inv.sgst)}
                    </td>

                    <td className="hidden whitespace-nowrap border-l border-l-violet-100 bg-violet-50/20 px-3 py-3 text-right md:table-cell">
                      {money(inv.igst)}
                    </td>

                    <td className="hidden whitespace-nowrap border-l border-l-violet-100 bg-violet-50/20 px-3 py-3 text-right font-semibold lg:table-cell">
                      {money(inv.gstTotal)}
                    </td>

                    <td className="whitespace-nowrap border-l-2 border-l-violet-200 bg-violet-50/50 px-3 py-3 text-right font-bold tabular-nums text-[#4b3d91]">
                      {money(inv.total)}
                    </td>

                    <td className="px-3 py-3 text-center">
                      <button
                        onClick={() => openDetails(inv)}
                        className="inline-flex items-center justify-center gap-1 rounded-full bg-[#6b59c6] px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:bg-[#5948ae] sm:px-3 sm:text-xs"
                        title="View details"
                      >
                        <FiEye />
                        <span className="hidden sm:inline">View</span>
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer pagination */}
      {!isLoading && !isFetching && rows.length > 0 && (
        <div className="flex shrink-0 items-center justify-between gap-2 border-t bg-white px-2 py-1.5 text-[10px] sm:gap-3 sm:px-4 sm:py-2 sm:text-[11px]">
          <div className="hidden text-slate-600 sm:block">
            Showing <span className="font-semibold">{showingFrom}</span>–
            <span className="font-semibold">{showingTo}</span> of{" "}
            <span className="font-semibold">{totalRecords}</span>
          </div>

          <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
            <div className="flex shrink-0 items-center gap-1">
              <span className="hidden text-slate-500 sm:inline">Rows:</span>
              <Select
                value={{ value: invoiceLimit, label: String(invoiceLimit) }}
                options={[10, 25, 50, 100].map((number) => ({
                  value: number,
                  label: String(number),
                }))}
                onChange={(option) => {
                  setInvoiceLimit(Number(option?.value) || 10);
                  setInvoicePage(1);
                }}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[54px]"
                aria-label="Rows per page"
              />
              <span className="text-[9px] text-slate-400 sm:hidden">
                / page
              </span>
            </div>

            <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none">
              <button
                type="button"
                onClick={() => setInvoicePage((page) => Math.max(page - 1, 1))}
                disabled={!hasPrevPage}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
                aria-label="Previous page"
              >
                <FiChevronLeft />
                <span className="hidden sm:inline">Prev</span>
              </button>

              <span className="text-slate-600">
                <span className="flex items-center gap-1 sm:hidden">
                  <Select
                    value={{ value: currentPage, label: String(currentPage) }}
                    options={Array.from(
                      { length: Math.max(1, totalPages) },
                      (_, index) => ({
                        value: index + 1,
                        label: String(index + 1),
                      }),
                    )}
                    onChange={(option) =>
                      setInvoicePage(Number(option?.value) || 1)
                    }
                    isSearchable={false}
                    menuPlacement="top"
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    menuShouldScrollIntoView={false}
                    styles={paginationSelectStyles}
                    className="w-[52px]"
                    aria-label="Select page number"
                  />
                  <span>/ {Math.max(1, totalPages)}</span>
                </span>
                <span className="hidden sm:inline">
                  Page <span className="font-semibold">{currentPage}</span> of{" "}
                  <span className="font-semibold">
                    {Math.max(1, totalPages)}
                  </span>
                </span>
              </span>

              <button
                type="button"
                onClick={() =>
                  setInvoicePage((page) =>
                    Math.min(page + 1, Math.max(1, totalPages)),
                  )
                }
                disabled={!hasNextPage}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
                aria-label="Next page"
              >
                <span className="hidden sm:inline">Next</span>
                <FiChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter panel */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] flex items-end justify-center bg-slate-950/40 p-3 backdrop-blur-[1px] sm:items-center sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsFilterOpen(false)}
          >
            <motion.div
              className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              initial={{ y: 30, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 30, opacity: 0, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-gradient-to-r from-[#6b59c6] to-[#9481ce] px-4 py-3 text-white">
                <div>
                  <h3 className="text-sm font-semibold">GST Filters</h3>
                  <p className="mt-0.5 text-[10px] text-violet-100">
                    Refine GST report by branch and date range
                  </p>
                </div>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                  onClick={() => setIsFilterOpen(false)}
                  aria-label="Close filters"
                >
                  <FiX />
                </button>
              </div>

              <div className="space-y-3 p-4">
                <div>
                  <label className="mb-1 block text-[10px] font-medium text-slate-600">
                    Branch
                  </label>
                  <Select
                    styles={selectStyles}
                    options={branchOptions}
                    isLoading={branchLoading}
                    menuPortalTarget={document.body}
                    value={
                      draftFilters.branchId
                        ? branchOptions.find((x) => x.value === draftFilters.branchId) ||
                          null
                        : null
                    }
                    onChange={(o) =>
                      setDraftFilters((s) => ({ ...s, branchId: o?.value || "" }))
                    }
                    placeholder="Select branch"
                    isClearable
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <div className="mb-1 text-[10px] font-medium text-slate-600">
                      From date
                    </div>
                    <input
                      type="date"
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-[11px] text-slate-700 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                      value={draftFilters.fromDate}
                      onChange={(e) =>
                        setDraftFilters((s) => ({
                          ...s,
                          fromDate: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div>
                    <div className="mb-1 text-[10px] font-medium text-slate-600">
                      To date
                    </div>
                    <input
                      type="date"
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-[11px] text-slate-700 outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-100"
                      value={draftFilters.toDate}
                      onChange={(e) =>
                        setDraftFilters((s) => ({
                          ...s,
                          toDate: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                {hasInvalidDateRange && (
                  <p className="text-[10px] font-medium text-rose-600">
                    The From date must be earlier than or equal to the To date.
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between gap-2 border-t bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={() => {
                    const cleared = { branchId: "", fromDate: "", toDate: "" };
                    setDraftFilters(cleared);
                  }}
                  className="text-[11px] font-semibold text-slate-500 hover:text-slate-700"
                >
                  Reset
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-[11px] font-semibold text-slate-600"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (hasInvalidDateRange) return;
                      setFilters(draftFilters);
                      setInvoicePage(1);
                      setIsFilterOpen(false);
                    }}
                    disabled={hasInvalidDateRange}
                    className="rounded-full bg-[#6b59c6] px-5 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#5948ae] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* DETAILS MODAL */}
      <GstDetailsModal
        open={detailOpen}
        invoice={selectedInvoice}
        itemDetails={itemDetails}
        onClose={() => {
          setDetailOpen(false);
          setSelectedInvoice(null);
        }}
      />
    </div>
  );
}

function FilterChip({ label }) {
  return (
    <span className="shrink-0 rounded-full border border-violet-100 bg-white px-2.5 py-1 text-[10px] font-medium text-slate-600">
      {label}
    </span>
  );
}
